import { z } from 'zod'
import {
  DEFAULTS,
  ORDER_CHANNELS,
  ORDER_STATUSES,
  ORDER_TYPES,
  PAYMENT_METHODS,
  PAYMENT_STATUSES,
} from '../constants'
import {
  localizedTextSchema,
  moneySchema,
  phoneSchema,
  timestampSchema,
  uuidSchema,
} from './common'

/** FR-ORD-1 — what a client is allowed to send. Note there is no price field: */
/** prices are resolved server-side from D1 (FR-ORD-2). */
export const cartLineInputSchema = z.object({
  productId: uuidSchema,
  variantId: uuidSchema.nullable().default(null),
  quantity: z.number().int().min(1).max(DEFAULTS.maxLineQuantity),
  modifierIds: z.array(uuidSchema).max(20).default([]),
  note: z.string().trim().max(DEFAULTS.maxNoteLength).nullable().default(null),
})
export type CartLineInput = z.infer<typeof cartLineInputSchema>

export const createOrderInputSchema = z.object({
  branchId: uuidSchema,
  channel: z.enum(ORDER_CHANNELS),
  type: z.enum(ORDER_TYPES),
  lines: z.array(cartLineInputSchema).min(1).max(50),
  couponCode: z.string().trim().max(32).nullable().default(null),
  note: z.string().trim().max(DEFAULTS.maxNoteLength).nullable().default(null),
  customerName: z.string().trim().min(1).max(60).nullable().default(null),
  customerPhone: phoneSchema.nullable().default(null),
  /** Signed table token from the QR code (FR-QR-1); required for the qr channel. */
  tableToken: z.string().nullable().default(null),
  /** FR-LOY-5 — points to spend on this order. Spending requires proof of
   * identity: a verified customer token, or a staff member keying the order. */
  redeemPoints: z.number().int().positive().nullable().default(null),
  customerToken: z.string().nullable().default(null),
  /** FR-REF-2 — a friend's referral code, honoured on the first completed order. */
  referralCode: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3,6}\d{2}$/)
    .nullable()
    .default(null),
})
export type CreateOrderInput = z.infer<typeof createOrderInputSchema>

export const orderItemSchema = z.object({
  id: uuidSchema,
  productId: uuidSchema,
  productName: localizedTextSchema,
  variantId: uuidSchema.nullable(),
  variantName: localizedTextSchema.nullable(),
  quantity: z.number().int(),
  unitPrice: moneySchema,
  lineTotal: moneySchema,
  modifiers: z.array(
    z.object({ id: uuidSchema, name: localizedTextSchema, priceDelta: z.number().int() }),
  ),
  spiceLevel: z.number().int().min(0).max(3).nullable(),
  note: z.string().nullable(),
})

export const orderSchema = z.object({
  id: uuidSchema,
  branchId: uuidSchema,
  orderNumber: z.string(),
  status: z.enum(ORDER_STATUSES),
  channel: z.enum(ORDER_CHANNELS),
  type: z.enum(ORDER_TYPES),
  tableId: uuidSchema.nullable(),
  tableLabel: z.string().nullable(),
  /** Link to the customer spine (FR-CRM-1); null when no valid phone given. */
  customerId: uuidSchema.nullable(),
  customerName: z.string().nullable(),
  customerPhone: z.string().nullable(),
  items: z.array(orderItemSchema),
  subtotal: moneySchema,
  discount: moneySchema,
  vat: moneySchema,
  total: moneySchema,
  couponCode: z.string().nullable(),
  pointsRedeemed: z.number().int().min(0).default(0),
  pointsDiscount: moneySchema.default(0),
  note: z.string().nullable(),
  isPaid: z.boolean(),
  isPriority: z.boolean(),
  estimatedWaitSeconds: z.number().int().nullable(),
  prepStartedAt: timestampSchema.nullable(),
  placedAt: timestampSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
})
export type Order = z.infer<typeof orderSchema>

/** Returned on creation: everything the success screen needs (FR-KIO-5). */
export const createOrderResultSchema = z.object({
  order: orderSchema,
  /** Tokenized status URL for the QR/kiosk receipt QR code (FR-QR-4). */
  statusToken: z.string(),
  estimatedWaitSeconds: z.number().int(),
})

export const updateOrderStatusInputSchema = z.object({
  status: z.enum(ORDER_STATUSES),
  reason: z.string().trim().max(200).optional(),
})

export const paymentInputSchema = z.object({
  orderId: uuidSchema,
  method: z.enum(PAYMENT_METHODS),
  amount: moneySchema,
  /** bKash/Nagad/Rocket transaction id, or the last 4 of a card. */
  reference: z.string().trim().max(64).nullable().default(null),
  /** Cash tendered, so the POS can show change due (FR-POS-3). */
  tendered: moneySchema.nullable().default(null),
})
export type PaymentInput = z.infer<typeof paymentInputSchema>

export const paymentSchema = z.object({
  id: uuidSchema,
  orderId: uuidSchema,
  method: z.enum(PAYMENT_METHODS),
  status: z.enum(PAYMENT_STATUSES),
  amount: moneySchema,
  reference: z.string().nullable(),
  confirmedByUserId: uuidSchema.nullable(),
  createdAt: timestampSchema,
})

export const refundInputSchema = z.object({
  orderId: uuidSchema,
  amount: moneySchema.nullable().default(null),
  reason: z.string().trim().min(3).max(200),
})
